<template>
  <ShortcutManager ref="shortcutManagerRef"></ShortcutManager>
  <loading :active="isRouteLoading" :can-cancel="false" :is-full-page="true"/>
  <component :is="layout" v-if="layout">
    <router-view  />
  </component>

  <dialog
    ref="unlockDialogEl"
    class="unlock-modal-dialog"
    @cancel.prevent="unlockValidationModal.close"
    @click.self="unlockValidationModal.close"
  >
    <UnlockValidationModal
      v-if="unlockValidationModalIsOpen"
      @close="unlockValidationModal.close"
      @success="unlockValidationModal.close"
    />
  </dialog>

  <NovaVersaoAviso />

  <Transition name="scroll-top">
    <button 
      v-if="showButton" 
      class="btn btn-fixed-top" 
      @click="goToTop"
      title="Voltar ao topo" 
    >
      <i class="flaticon-up-arrow"></i>
    </button>
  </Transition>

  <button
      class="btn btn-fixed-bottom"
      @click="goToBottom"
      title="Ir para baixo"
    >
      <i class="flaticon-down-arrow"></i>
  </button>
</template>

<script setup>
import { computed, watch, onMounted, onUnmounted, ref, provide } from 'vue';
import { useRouter, START_LOCATION } from 'vue-router';
import Loading from '@/plugins/vueLoading.js';
import ShortcutManager from '@/components/common/ShortcutManager.vue';
import UnlockValidationModal from '@/components/common/UnlockValidationModal.vue';
import NovaVersaoAviso from '@/components/common/NovaVersaoAviso.vue';
import unlockValidationModalStore from '@/stores/unlockValidationModalStore';
import routeLoadingStore from '@/stores/routeLoadingStore.js';

// Criar uma referência para o ShortcutManager
const shortcutManagerRef  = ref(null);


const { currentRoute } = useRouter();

const defaultLayout = 'default';
// Até a navegação inicial resolver, currentRoute é o START_LOCATION, sem meta. Cair no default
// ali montava o shell (header → MainNotification) por um instante mesmo em rota de layout
// vazio — o bastante para disparar /api/notificacao/ultimas e abrir o SignalR. Sem rota
// resolvida, não há layout para renderizar.
const layout = computed(() => {
  if (currentRoute.value === START_LOCATION) return null;
  return `${currentRoute.value.meta.layout || defaultLayout}-layout`;
});
const unlockValidationModal = unlockValidationModalStore;
const unlockValidationModalIsOpen = computed(() => unlockValidationModal.isOpen.value);
const isRouteLoading = computed(() => routeLoadingStore.isLoading.value);

const unlockDialogEl = ref(null);

watch(unlockValidationModalIsOpen, (isOpen) => {
  if (isOpen) unlockDialogEl.value?.showModal();
  else        unlockDialogEl.value?.close();
});

onMounted(() => {
  //console.log('mounted', shortcutManagerRef.value)
  //shortcutManagerRef.value.registerGroupShortcut('Geral', 'Usados em qualquer lugar')

  // Aqui você pode registrar atalhos diretamente
  //shortcutManagerRef.value.registerShortcut('Alt + F1', () => { alert('eita')},'Ajuda', 'Geral');
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

// Fornecer a função registerShortcut para outros componentes
provide('registerShortcut', (keyCombo, action, description, group, keyUpAction) => {
  if (shortcutManagerRef.value) {
    shortcutManagerRef.value.registerShortcut(keyCombo, action, description, group, keyUpAction);
  }
});


provide('registerGroupShortcut', (title, description) => {
  if (shortcutManagerRef.value) {
    shortcutManagerRef.value.registerGroupShortcut(title, description);
  }
});


provide('unregisterShortcut', (keyCombo) => {
  if (shortcutManagerRef.value) {
    shortcutManagerRef.value.unregisterShortcut(keyCombo);
  }
});

// Até aqui a lista de atalhos só existia para quem já soubesse que Alt + F1 a abre.
// O botão no cabeçalho usa isto.
provide('abrirAjudaDeAtalhos', () => {
  shortcutManagerRef.value?.abrirAjuda();
});

/* Scroll to top button logic */
const showButton = ref(false)

const handleScroll = () => {
  showButton.value = window.scrollY > 50
}

const goToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const goToBottom = () => {
  window.scrollTo({ top: 999999, behavior: 'smooth' })
}
</script>

<style scoped>
body {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

/* Button scroll

   O valor aqui era #0b1a34 com o var(--prana-azul) comentado ao lado — e o hex e exatamente o
   valor daquele token, entao nao havia diferenca visual, so token virado literal.

   Mas a cor certa nao era a Prana: "Prana e um escopo, nao um tema", e estes botoes de rolagem
   sao chrome Triveni geral, nao superficie da Prana Comex. O equivalente Triveni e
   --surface-inverse (= --triveni-navy, #0E1A36), praticamente a mesma cor. Medido no claro com
   a opacity de 0,8 ja composta: o botao efetivo fica #3c455d, 8,51 contra a pagina, e o icone
   (agora var(--text-on-dark)) da 17,22 dentro dele.

   CONHECIDO, e anterior a esta troca: contra a pagina ESCURA (#171717) esse navy da 1,03 — o
   botao some. O --triveni-navy nao muda isso (1,04). Tornar o botao visivel no escuro pede
   regra .dark e uma decisao de desenho sobre a cara dele ali, que nao foi tomada. */
.btn-fixed-top {
  background-color: var(--surface-inverse);
  padding: 0.5rem 0.75rem ;
  position: fixed;
  bottom: 60px;
  right: 20px;
  z-index: 10;
  opacity: 0.8;
}
.btn-fixed-bottom {
  background-color: var(--surface-inverse);
  padding: 0.5rem 0.75rem ;
  position: fixed;
  bottom: 10px;
  right: 20px; 
  z-index: 10;
  opacity: 0.8;
}

.btn-fixed-top:hover, .btn-fixed-bottom:hover {
  background-color: var(--surface-inverse);
  opacity: 1;
}

.btn-fixed-top > i, .btn-fixed-bottom > i {
  color: var(--text-on-dark);
}

/* https://vuejs.org/guide/built-ins/transition */
.scroll-top-enter-active,
.scroll-top-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.scroll-top-enter-from,
.scroll-top-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

.scroll-top-enter-to,
.scroll-top-leave-from {
  opacity: 0.7;
  transform: translateY(0);
}

/* Modal - Liberação Gerência Processo */
dialog.unlock-modal-dialog {
  border: none;
  background: transparent;
  padding: 1rem;
  /* max-width: 640px; */
  width: 100%;
}

dialog.unlock-modal-dialog::backdrop {
  background: rgba(0, 0, 0, 0.55);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
